import { ModalProps } from '../../../../services/modals.service';
import {
  CleanUser,
  CleanUserAccess,
  TempAccessSession,
} from '../../../../export-types/cleaned-types';
import { Controller, useForm } from 'react-hook-form';
import { AlertService } from '../../../../services/alert.service';
import { useModalClose } from '../../../../utils/useModalClose';
import { required } from '../../../../utils/validations';
import { InputWrapper } from '../../../common/input-wrapper';
import { User, UserSelect } from '../../../common/user-select';
import { trpcSwr } from '../../../../api/trpc';
import { Checkbox } from '../../../common/checkbox';
import React, { useState } from 'react';
import { DatePicker, TimePicker } from 'rsuite';
import { useIntl } from 'react-intl';
import moment from 'moment';

interface Props extends ModalProps {
  user: CleanUser;
  session?: TempAccessSession;
  mutate?: () => void;
}

export const TempAccessSessionModal = ({
  user,
  close,
  session,
  mutate,
}: Props) => {
  const intl = useIntl();
  const {
    control,
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    watch,
  } = useForm<{
    user: User;
    startedAt: Date;
    finishedAt: Date;
    reason: string;
    accesses: CleanUserAccess[];
  }>({
    defaultValues: {
      startedAt: session?.schedule[0].startedAt || new Date(),
      finishedAt: session?.schedule[0].finishedAt,
      user: { ...user, name: user.name || '' },
      reason: session?.reason,
    },
  });

  const startedAt = watch('startedAt');

  const [isFast, setIsFast] = useState((session?.schedule?.length || 1) <= 1);

  const [selectedDates, setSelectedDates] = useState<Array<Date>>(
    session?.schedule?.map((date) => date.startedAt) || [],
  );

  const handleDateSelect = (date: Date | null) => {
    if (!date) return;

    const dateString = date.toISOString().split('T')[0];
    setSelectedDates((prev) => {
      const exists = prev.some(
        (d) => d.toISOString().split('T')[0] === dateString,
      );

      if (exists) {
        return prev.filter((d) => d.toISOString().split('T')[0] !== dateString);
      } else {
        return [...prev, date];
      }
    });
  };

  const isDateSelected = (date: Date) => {
    return selectedDates.some(
      (selectedDate) => selectedDate.toDateString() === date.toDateString(),
    );
  };

  const [purchase, setPurchase] = useState(
    session?.accesses?.includes('purchase') || false,
  );
  const [warehouse, setWarehouse] = useState(
    session?.accesses?.includes('warehouse') || false,
  );
  const [salesManager, setSalesManager] = useState(
    session?.accesses?.includes('salesManager') || false,
  );
  const [supplierFinances, setSupplierFinances] = useState(
    session?.accesses?.includes('supplierFinances') || false,
  );
  const [marketer, setMarketer] = useState(
    session?.accesses?.includes('marketer') || false,
  );
  const [transit, setTransit] = useState(
    session?.accesses?.includes('transit') || false,
  );

  const [financier, setFinancier] = useState(
    session?.accesses?.includes('financier') || false,
  );

  const [reader, setReader] = useState(
    session?.accesses?.includes('reader') || false,
  );

  const [fulfillment, setFulfillment] = useState(
    session?.accesses?.includes('fulfillment') || false,
  );

  const [warehouseRussia, setWarehouseRussia] = useState(
    session?.accesses?.includes('warehouseRussia') || false,
  );

  const [customsDeliveryManager, setCustomsDeliveryManager] = useState(
    user?.accesses?.includes('customsDeliveryManager') || false,
  );

  const [declarant, setDeclarant] = useState(
    user?.accesses?.includes('declarant') || false,
  );

  const { trigger: create } = trpcSwr.tempAccessSession.create.useSWRMutation();
  const { trigger: update } = trpcSwr.tempAccessSession.update.useSWRMutation();

  const onSubmit = handleSubmit(
    async ({ user, startedAt, finishedAt, reason }) => {
      const accesses = [];

      if (purchase) {
        accesses.push('purchase');
      }
      if (warehouse) {
        accesses.push('warehouse');
      }
      if (salesManager) {
        accesses.push('salesManager');
      }
      if (supplierFinances) {
        accesses.push('supplierFinances');
      }
      if (marketer) {
        accesses.push('marketer');
      }
      if (transit) {
        accesses.push('transit');
      }
      if (financier) {
        accesses.push('financier');
      }
      if (reader) {
        accesses.push('reader');
      }
      if (fulfillment) {
        accesses.push('fulfillment');
      }

      if (warehouseRussia) {
        accesses.push('warehouseRussia');
      }

      if (customsDeliveryManager) {
        accesses.push('customsDeliveryManager');
      }

      if (declarant) {
        accesses.push('declarant');
      }

      if (!accesses.length) {
        AlertService.error('Выберите хотя бы одну роль');
      }

      let dates = selectedDates.map((date) => ({
        startedAt: moment(date)
          .hour(moment(startedAt).hours())
          .minutes(moment(startedAt).minutes())
          .toDate(),
        finishedAt: moment(date)
          .hour(moment(finishedAt).hours())
          .minutes(moment(finishedAt).minutes())
          .toDate(),
      }));
      if (!dates.length || dates.length === 1) {
        dates = [
          {
            startedAt: startedAt,
            finishedAt: finishedAt,
          },
        ];
      }
      if (session) {
        await update({
          id: session.id,
          data: {
            userId: user.id,
            accesses: accesses as CleanUserAccess[],
            reason,
            dates,
          },
        }).then(() => {
          AlertService.success();
          mutate?.();
          close();
        });
      } else {
        await create({
          data: {
            userId: user.id,
            accesses: accesses as CleanUserAccess[],
            reason,
            dates,
          },
        }).then(() => {
          AlertService.success();
          mutate?.();
          close();
        });
      }
    },
  );

  const modalRef = useModalClose(close);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <div className="modal-content">
        <div className="modal-header border-bottom-0 p-4 d-flex gap-2">
          <div className="text-left mx-auto mt-1">
            <h5>Выдать права для пользователя</h5>
          </div>
          <button type="button" className="btn-close" onClick={() => close()} />
        </div>
        <div className="modal-body pt-2 p-4">
          <form onSubmit={onSubmit}>
            <Controller
              control={control}
              name="user"
              rules={{
                ...required,
              }}
              render={({ field, fieldState }) => (
                <InputWrapper
                  className="mb-3"
                  title="Сотрудник"
                  required
                  error={fieldState.error?.message}
                >
                  <UserSelect
                    placeholder="Выберите сотрудника"
                    {...field}
                    roles={['employee']}
                  />
                </InputWrapper>
              )}
            />
            <div className="mb-4">
              <div className="mb-2">
                Права <span className="color-red">*</span>
              </div>
              <div className="d-flex flex-row flex-wrap justify-content-between mb-4">
                <div>
                  <Checkbox
                    title="Закупка"
                    className=""
                    checked={purchase}
                    onChange={({ target: { checked } }) => setPurchase(checked)}
                  />
                  <Checkbox
                    title="Менеджер по продажам"
                    checked={salesManager}
                    onChange={({ target: { checked } }) =>
                      setSalesManager(checked)
                    }
                  />
                  <Checkbox
                    title="Маркетолог"
                    checked={marketer}
                    onChange={({ target: { checked } }) => setMarketer(checked)}
                  />
                  <Checkbox
                    title="Читатель"
                    checked={reader}
                    onChange={({ target: { checked } }) => setReader(checked)}
                  />
                  <Checkbox
                    title="Финансист"
                    checked={financier}
                    onChange={({ target: { checked } }) =>
                      setFinancier(checked)
                    }
                  />
                </div>
                <div>
                  <Checkbox
                    title="Склад"
                    className=""
                    checked={warehouse}
                    onChange={({ target: { checked } }) =>
                      setWarehouse(checked)
                    }
                  />
                  <Checkbox
                    title="Склад: транзит"
                    checked={transit}
                    onChange={({ target: { checked } }) => setTransit(checked)}
                  />
                  <Checkbox
                    title="Склад: фулфилмент"
                    checked={fulfillment}
                    onChange={({ target: { checked } }) =>
                      setFulfillment(checked)
                    }
                  />
                  <Checkbox
                    title="Поставщик: финансы"
                    checked={supplierFinances}
                    onChange={({ target: { checked } }) =>
                      setSupplierFinances(checked)
                    }
                  />
                  <Checkbox
                    title="Склад  РФ"
                    checked={warehouseRussia}
                    onChange={({ target: { checked } }) =>
                      setWarehouseRussia(checked)
                    }
                  />

                  <Checkbox
                    title="Менеджер  ВЭД"
                    checked={customsDeliveryManager}
                    onChange={({ target: { checked } }) =>
                      setCustomsDeliveryManager(checked)
                    }
                  />

                  <Checkbox
                    title="Декларант"
                    checked={declarant}
                    onChange={({ target: { checked } }) =>
                      setDeclarant(checked)
                    }
                  />
                </div>
              </div>
              <InputWrapper
                title="Причина"
                className="mb-4"
                error={errors.reason?.message}
                required
              >
                <textarea
                  placeholder="Выгрузить отчет"
                  className="form-control "
                  {...register('reason', { ...required })}
                />
              </InputWrapper>

              <div className={'d-flex gap-2 mb-2'}>
                <Checkbox
                  className="d-inline-block"
                  title="Быстрый доступ"
                  checked={isFast}
                  onChange={(event) => {
                    setSelectedDates([]);
                    setIsFast(event.target.checked);
                  }}
                />
                <Checkbox
                  className="d-inline-block"
                  title="Расширенный доступ"
                  checked={!isFast}
                  onChange={(event) => {
                    setIsFast(!event.target.checked);
                  }}
                />
              </div>

              {isFast ? (
                <>
                  <div className={'d-flex gap-2'}>
                    <Controller
                      name="startedAt"
                      control={control}
                      render={({ field, fieldState }) => {
                        return (
                          <InputWrapper
                            title={intl.formatMessage({
                              defaultMessage: 'Время начала',
                              id: 'tempAccessSessionModal.input-label.startedAt',
                              description: 'Заголовок поля ввода',
                            })}
                            error={fieldState.error?.message}
                          >
                            <DatePicker
                              className={'w-100'}
                              format={'dd.MM.yyyy HH:mm'}
                              isoWeek={true}
                              onChange={(v) => {
                                if (!v) {
                                  field.onChange(null);
                                } else {
                                  field.onChange(v);
                                }
                              }}
                              value={field.value}
                            ></DatePicker>
                          </InputWrapper>
                        );
                      }}
                    />

                    <Controller
                      name="finishedAt"
                      control={control}
                      render={({ field, fieldState }) => {
                        return (
                          <InputWrapper
                            title={intl.formatMessage({
                              defaultMessage: 'Время завершения',
                              id: 'tempAccessSessionModal.input-label.finishedAt',
                              description: 'Заголовок поля ввода',
                            })}
                            error={fieldState.error?.message}
                          >
                            <DatePicker
                              className={'w-100'}
                              format={'dd.MM.yyyy HH:mm'}
                              isoWeek={true}
                              onChange={(v) => {
                                if (!v) {
                                  field.onChange(null);
                                } else {
                                  field.onChange(v);
                                }
                              }}
                              value={field.value}
                            ></DatePicker>
                          </InputWrapper>
                        );
                      }}
                    />
                  </div>
                  <div className={'d-flex gap-2 mt-3'}>
                    <button
                      type="button"
                      className="btn btn-outline-success w-100"
                      onClick={() => {
                        setValue(
                          'finishedAt',
                          moment(startedAt).add(1, 'hours').toDate(),
                        );
                      }}
                    >
                      на 1 час
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success w-100"
                      onClick={() => {
                        setValue(
                          'finishedAt',
                          moment(startedAt).add(8, 'hours').toDate(),
                        );
                      }}
                    >
                      на 8 час
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success w-100"
                      onClick={() => {
                        setValue(
                          'finishedAt',
                          moment(startedAt).add(1, 'days').toDate(),
                        );
                      }}
                    >
                      на 24 час
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success w-100"
                      onClick={() => {
                        setValue(
                          'finishedAt',
                          moment(startedAt).add(7, 'days').toDate(),
                        );
                      }}
                    >
                      на 7 дней
                    </button>
                  </div>
                </>
              ) : (
                <div>
                  <InputWrapper
                    title={intl.formatMessage({
                      defaultMessage: 'Выберите даты',
                      id: 'tempAccessSessionModal.input-label.dates',
                      description: 'Заголовок поля ввода',
                    })}
                  >
                    <DatePicker
                      className={'w-100'}
                      format={'dd.MM.yyyy'}
                      isoWeek={true}
                      value={null}
                      renderCell={(date) => {
                        const isSelected = isDateSelected(date);
                        return (
                          <div
                            className={
                              isSelected
                                ? 'bg-green-500 text-white rounded'
                                : ''
                            }
                          >
                            {date.getDate()}
                          </div>
                        );
                      }}
                      onSelect={(value) => {
                        handleDateSelect(value);
                      }}
                    ></DatePicker>
                  </InputWrapper>
                  <div className={'d-flex gap-2'}>
                    <Controller
                      name="startedAt"
                      control={control}
                      render={({ field, fieldState }) => {
                        return (
                          <InputWrapper
                            title={intl.formatMessage({
                              defaultMessage: 'Время начала',
                              id: 'tempAccessSessionModal.input-label.startedAt',
                              description: 'Заголовок поля ввода',
                            })}
                            error={fieldState.error?.message}
                          >
                            <TimePicker
                              className={'w-100'}
                              format={'HH:mm'}
                              onChange={(v) => {
                                if (!v) {
                                  field.onChange(null);
                                } else {
                                  field.onChange(v);
                                }
                              }}
                              defaultValue={field.value}
                            ></TimePicker>
                          </InputWrapper>
                        );
                      }}
                    />

                    <Controller
                      name="finishedAt"
                      control={control}
                      render={({ field, fieldState }) => {
                        return (
                          <InputWrapper
                            title={intl.formatMessage({
                              defaultMessage: 'Время завершения',
                              id: 'tempAccessSessionModal.input-label.finishedAt',
                              description: 'Заголовок поля ввода',
                            })}
                            error={fieldState.error?.message}
                          >
                            <TimePicker
                              className={'w-100'}
                              format={'HH:mm'}
                              onChange={(v) => {
                                if (!v) {
                                  field.onChange(null);
                                } else {
                                  field.onChange(v);
                                }
                              }}
                              defaultValue={field.value}
                            ></TimePicker>
                          </InputWrapper>
                        );
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="btn btn-success w-100 mb-3 mt-5">
              Добавить
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
